// CT 17 reference popup — complexity tiers (by business sector) + man-day table.
// Values VERIFIED against CT 17 (24 Jul 2024) base table + IAF MD 5:2023 Table OH&SMS 2. Multi-site uses
// CT 17's 10% rule, which OVERRIDES IAF MD 1's √x for bizSAFE RM. Read-only reference for staff.
import React, { useState } from 'react'

const HIGH = [
  'Fishing (offshore, coastal dredging and diving)',
  'Mining and quarrying',
  'Manufacture of coke and refined petroleum products',
  'Oil and gas extraction',
  'Tanning of textiles and clothing',
  'Pulping part of paper manufacturing (incl. paper recycling)',
  'Oil refining',
  'Chemicals (incl. pesticides, batteries, accumulators) and pharmaceuticals',
  'Manufacturing of fibreglass',
  'Gas production, storage and distribution',
  'Electricity generation and distribution',
  'Nuclear',
  'Storage of large quantities of hazardous material',
  'Non-metallic processing/products: ceramics, concrete, cement, lime, plaster',
  'Primary production of metals',
  'Hot and cold forming and metal fabrication',
  'Manufacturing and assembly of metal structures',
  'Aerospace industry',
  'Automotive industry',
  'Manufacturing of weapons and explosives',
  'Recycling of hazardous waste',
  'Hazardous and non-hazardous waste processing (e.g. incineration)',
  'Effluent and sewerage processing',
  'Industrial and civil construction and demolition (incl. building completion: electrical, hydraulic, air-conditioning installation)',
  'Slaughter houses',
  'Transport and distribution of dangerous goods (land, air, water)',
  'Defence activities / crisis management',
  'Healthcare / hospitals / veterinary / social works',
]

const MEDIUM = [
  'Aquaculture',
  'Fishing (offshore fishing is High)',
  'Farming / forestry (could be High depending on activities)',
  'Food, beverage and tobacco — processing',
  'Textiles and clothing (except tanning)',
  'Manufacturing of wood and wooden products (incl. boards, wood treatment)',
  'Paper production and paper products (excluding pulping)',
  'Non-metallic processing/products: glass, ceramics, clay',
  'General mechanical engineering assembly',
  'Manufacturing of metallic products',
  'Surface/chemical treatment of metal fabricated products (could be High)',
  'Production of bare printed circuit boards',
  'Rubber and plastic injection moulding, forming and assembly',
  'Electrical and electronic equipment assembly',
  'Manufacturing of transport equipment and repairs — road, rail, air (could be High)',
  'Recycling, composting, landfill (non-hazardous waste)',
  'Water abstraction, purification and distribution (commercial effluent is High)',
  'Fossil fuel wholesale and retail (could be High)',
  'Transport of passengers (air, land, sea)',
  'Transport and distribution of non-dangerous goods (land, air, water)',
  'Industrial cleaning, hygiene cleaning, dry cleaning',
  'R&D in natural and technical sciences (could be High); technical testing and laboratories',
  'Hotels, leisure and personal services (excludes restaurants)',
  'Education services (could be High or Low)',
]

const LOW = [
  'Corporate activities and management, HQ, management of holding companies',
  'Wholesale and retail (could be Medium/High depending on product, e.g. fuel)',
  'General business services (except industrial/hygiene/dry cleaning and education)',
  'Transport and distribution — management services with no actual fleet',
  'Engineering services (could be Medium depending on type)',
  'Telecommunications and post office services',
  'Restaurants and campings',
  'Commercial estate agency, estate management',
  'R&D on social sciences and humanities',
  'Public administration, local authorities',
  'Financial institutions, advertising agency',
]

// Fixed-scope bizSAFE RM man-days (from ct17.js) — named scopes use these directly; no complexity needed.
const FIXED = [
  { scope: 'Construction Worksite', le200: '1.0', le500: '1.5', gt500: '2.0' },
  { scope: 'Metalworking Industry', le200: '1.0', le500: '1.0', gt500: '1.0' },
  { scope: 'Shipyard', le200: '1.0', le500: '1.0', gt500: '2.0' },
  { scope: 'Oil Refinery / Petrochemical · Semiconductor · Chemical · Pharmaceutical · Bulk Storage', le200: '2.0', le500: '2.0', gt500: '2.0' },
]

// bizSAFE RM base man-days for "Other Industries" by complexity × employees (from ct17.js).
const MANDAYS = [
  { tier: 'High',   le200: '1.0', le500: '1.5', gt500: '2.0', color: '#791F1F', bg: '#FDECEA' },
  { tier: 'Medium', le200: '0.5', le500: '1.0', gt500: '1.5', color: '#854F0B', bg: '#FFF3E0' },
  { tier: 'Low',    le200: '0.5', le500: '0.5', gt500: '1.0', color: '#27500a', bg: '#EAF3DE' },
]

function SectorBlock({ title, items, color, bg }) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer', padding: '8px 14px', background: bg, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 13, color }}>{title} complexity</span>
        <span style={{ fontSize: 13, color }}>{open ? '▲' : `▼ ${items.length} sectors`}</span>
      </div>
      {open && (
        <ul style={{ margin: 0, padding: '10px 14px 12px 30px', fontSize: 13, color: 'var(--text)', lineHeight: 1.6 }}>
          {items.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
    </div>
  )
}

export default function CT17Reference({ label }) {
  const [open, setOpen] = useState(false)
  const th = { textAlign: 'left', padding: '6px 10px', fontWeight: 600, fontSize: 13, color: 'var(--text2)', borderBottom: '1px solid var(--border)' }
  const td = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid var(--border)' }
  return (
    <>
      <button className="sm" type="button" onClick={() => setOpen(true)} style={{ fontSize: 13 }}>
        📖 {label || 'CT 17 reference — complexity & man-days'}
      </button>
      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal" style={{ maxWidth: 760, maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>CT 17 reference — complexity &amp; man-days</h2>
              <button onClick={() => setOpen(false)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--text3)' }}>×</button>
            </div>
            <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
              <strong>Named scopes</strong> (Construction, Metalworking, Shipyard, Oil/Petrochemical, etc.) use the fixed man-days in the first table. <strong>“Other Industries”</strong> uses the complexity table below it. Values <strong>verified against CT 17 (24 Jul 2024) + IAF MD 5:2023</strong>; always confirm against your current CT 17 copy.
            </p>

            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Fixed-scope man-days — named scopes (use directly)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={th}>Audit scope</th>
                  <th style={th}>≤ 200 employees</th>
                  <th style={th}>201–500 employees</th>
                  <th style={th}>&gt; 500 employees</th>
                </tr>
              </thead>
              <tbody>
                {FIXED.map(r => (
                  <tr key={r.scope}>
                    <td style={{ ...td, fontWeight: 500 }}>{r.scope}</td>
                    <td style={td}>{r.le200}</td>
                    <td style={td}>{r.le500}</td>
                    <td style={td}>{r.gt500}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>“Other Industries” man-days (base, by complexity × employees)</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
              <thead>
                <tr>
                  <th style={th}>Complexity</th>
                  <th style={th}>≤ 200 employees</th>
                  <th style={th}>201–500 employees</th>
                  <th style={th}>&gt; 500 employees</th>
                </tr>
              </thead>
              <tbody>
                {MANDAYS.map(r => (
                  <tr key={r.tier}>
                    <td style={td}><span style={{ fontSize: 13, fontWeight: 600, padding: '2px 8px', borderRadius: 12, background: r.bg, color: r.color }}>{r.tier}</span></td>
                    <td style={td}>{r.le200}</td>
                    <td style={td}>{r.le500}</td>
                    <td style={td}>{r.gt500}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ul style={{ margin: '0 0 18px', paddingLeft: 18, fontSize: 13, color: 'var(--text2)', lineHeight: 1.7 }}>
              <li><strong>Lead Auditor</strong> required when &gt; 200 employees.</li>
              <li><strong>Multi-site (CT 17 scheme requirement — overrides IAF MD 1):</strong> visit at least 10% of worksites (min 1); add 0.5 man-day per additional site visited.</li>
              <li>Pending Review / conditional sectors: confirm complexity manually on the quotation (“Override classification”).</li>
            </ul>

            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Complexity by business sector</h3>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 10 }}>Click a tier to expand. Highest applicable hazard governs.</p>
            <SectorBlock title="High"   items={HIGH}   color="#791F1F" bg="#FDECEA" />
            <SectorBlock title="Medium" items={MEDIUM} color="#854F0B" bg="#FFF3E0" />
            <SectorBlock title="Low"    items={LOW}    color="#27500a" bg="#EAF3DE" />

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
